/**
 * Client request detail — the client-side parallel of the admin triage
 * view: basic facts up top, the CONVERSATION as the main content. The
 * chat thread is the same request-anchored client thread analysts see
 * in the triage workspace, so both sides talk in one place.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Pill,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconPlayerPlay,
  IconReportAnalytics,
  IconUserCheck,
} from '@tabler/icons-react';

import { ClientReferenceRow } from '@/components';
import { CaseSharing } from '@/components/CaseSharing';
import { ConversationView } from '@/components/Chat';
import { useAuth } from '@/contexts/AuthContext';
import { useClientReportRequest } from '@/hooks';
import { ApiConflictError, vnextApiClient } from '@/services/vnext-api';
import { isClientAnalyst } from '@/types/auth';
import { displayedRequestStatus } from '@/types/report-request';
import { formatDueDate, formatMoney } from '@/utils';

/** Wall-clock instants (when something happened) — local zone is right
 *  here; due dates go through `formatDueDate` instead. */
function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return '-';
  }
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function RequestDetailPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const { request, isLoading, error, refetch, updateReference } = useClientReportRequest(requestId);
  const [activating, setActivating] = useState(false);
  const { user } = useAuth();
  // Client analysts can submit and follow requests but starting
  // screening is an admin / project-manager decision (API 403s too).
  const canActivate = !isClientAnalyst(user);

  const handleActivate = async (reportId: string) => {
    setActivating(true);
    try {
      await vnextApiClient.activateReport(reportId);
      notifications.show({
        title: 'Screening started',
        message: 'Your report is now being generated.',
        color: 'green',
      });
      await refetch();
    } catch (err) {
      // 409 — already started elsewhere; just refresh into that state.
      if (err instanceof ApiConflictError) {
        await refetch();
        return;
      }
      notifications.show({
        title: 'Could not start screening',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'red',
      });
    } finally {
      setActivating(false);
    }
  };

  if (isLoading && !request) {
    return (
      <Center h={400}>
        <Loader />
      </Center>
    );
  }

  if (error || !request) {
    return (
      <Container size="lg" py="md">
        <Text c="red">{error?.message ?? 'Request not found'}</Text>
      </Container>
    );
  }

  // The linked report's coarse status is hoisted over the RR-level one
  // once a client-visible report exists (same rule as the list rows).
  const status = displayedRequestStatus(request);
  const awaitingActivation = request.reportStatus === 'pending_activation' && !!request.reportId;
  // The profile-review gate is the requesting team's to clear, any role
  // included, so the button carries no permission check.
  const awaitingProfiles = request.reportStatus === 'awaiting_profile_review' && !!request.reportId;
  const facts: string[] = [
    ...(request.configName ? [request.configName] : []),
    ...(formatMoney(request.price, request.currency)
      ? [formatMoney(request.price, request.currency) as string]
      : []),
    // Once screening starts the concrete due date replaces the TAT.
    ...(request.dueAt
      ? [`Due ${formatDueDate(request.dueAt)}`]
      : request.turnaroundTimeDays != null
        ? [`${request.turnaroundTimeDays}d turnaround`]
        : []),
    ...(request.jurisdictions.length ? [request.jurisdictions.join(', ')] : []),
    ...(request.requesterEmail ? [`Requested by ${request.requesterEmail}`] : []),
    `Requested ${formatDate(request.createdAt)}`,
  ];

  return (
    <Container size="lg" py="md">
      <Stack gap="md">
        <Anchor component={Link} to="/requests" size="sm">
          <Group gap={4} wrap="nowrap">
            <IconArrowLeft size={14} />
            All requests
          </Group>
        </Anchor>

        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap={6}>
            <Group gap="sm" wrap="wrap">
              {/* Clients identify requests by their own reference —
                  codenames are admin-only. */}
              <Title order={2}>{request.clientReference ?? 'Report request'}</Title>
              <Badge size="lg" variant="light" color={status.color} data-testid="request-status">
                {status.label}
              </Badge>
              {request.expedited && (
                <Badge size="lg" variant="light" color="orange">
                  Expedited
                </Badge>
              )}
            </Group>
            <Group gap={6} wrap="wrap">
              {facts.map((fact, i) => (
                <Group key={fact} gap={6} wrap="nowrap">
                  {i > 0 && (
                    <Text size="sm" c="dimmed">
                      ·
                    </Text>
                  )}
                  <Text size="sm" c="dimmed">
                    {fact}
                  </Text>
                </Group>
              ))}
            </Group>
            <ClientReferenceRow value={request.clientReference} onSave={updateReference} />
            {request.subjects.length > 0 && (
              <Group gap={4} wrap="wrap">
                {request.subjects.map((s) => (
                  <Pill
                    key={s}
                    size="sm"
                    maw={420}
                    title={s}
                    styles={{ label: { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                  >
                    {s}
                  </Pill>
                ))}
              </Group>
            )}
            <CaseSharing anchor={{ kind: 'request', id: request.id }} />
          </Stack>
          {request.reportId && (
            <Group gap="xs" wrap="nowrap">
              {awaitingActivation && canActivate && (
                <Button
                  color="green"
                  leftSection={<IconPlayerPlay size={16} />}
                  loading={activating}
                  onClick={() => void handleActivate(request.reportId as string)}
                  data-testid="request-start-screening"
                >
                  Start screening
                </Button>
              )}
              {awaitingActivation && !canActivate && (
                <Text size="sm" c="dimmed" data-testid="request-activation-hint">
                  Ask your admin or project manager to start this report.
                </Text>
              )}
              {awaitingProfiles && (
                <Button
                  component={Link}
                  to={`/reports/${request.reportId}?aside=profile-review`}
                  color="orange"
                  leftSection={<IconUserCheck size={16} />}
                  data-testid="request-confirm-profiles"
                >
                  Confirm profiles
                </Button>
              )}
              <Button
                component={Link}
                to={`/reports/${request.reportId}`}
                variant={awaitingActivation || awaitingProfiles ? 'default' : 'filled'}
                leftSection={<IconReportAnalytics size={16} />}
                data-testid="request-view-report"
              >
                View report
              </Button>
            </Group>
          )}
        </Group>

        <Card shadow="sm" padding="lg" radius="md">
          <Stack gap="sm" style={{ minHeight: '55vh', display: 'flex' }}>
            <Text size="sm" fw={600}>
              Conversation
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: '45vh' }}>
              <ConversationView
                entityType="report_request"
                entityId={request.id}
                kind="client_thread"
                canSendPrivate={false}
              />
            </div>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
